import { RxCrossCircled } from "react-icons/rx";
import { IoIosArrowDown, IoIosClose } from "react-icons/io";
import { MdLockOutline } from "react-icons/md";
import { FaSearch, FaGripLines } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useRef, useState } from "react";

export function ModifyColumns({ columns, setColumns, setShowColumnsMenu }) {
  const [showCustomColumnForm, setShowCustomColumnForm] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const columnSearchRef = useRef(null);

  const uniqueCategories = Array.from(
    new Set(columns.map((col) => col.category))
  );

  const categoriesToMoveDown = ["Custom Columns", "Budget Name"];
  const orderedCategories = [
    ...uniqueCategories.filter((cat) => !categoriesToMoveDown.includes(cat)),
    ...uniqueCategories.filter((cat) => categoriesToMoveDown.includes(cat)),
  ];

  const toggleCategory = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const toggleColumnVisibility = (key) => {
    setColumns(
      columns.map((col) =>
        col.key === key && !col.locked ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const visibleColumns = columns.filter((col) => col.visible);
    const hiddenColumns = columns.filter((col) => !col.visible);

    const [movedItem] = visibleColumns.splice(source.index, 1);
    visibleColumns.splice(destination.index, 0, movedItem);

    setColumns([...visibleColumns, ...hiddenColumns]);
  };

  const ColumnItem = ({ column }) => (
    <div className="flex flex-row items-center justify-between p-1.5 mb-1 rounded cursor-pointer bg-white shadow-sm hover:bg-slate-100 text-sm">
      <div>
        <input
          type="checkbox"
          checked={column.visible}
          onChange={() => toggleColumnVisibility(column.key)}
          className="mr-1.5"
          disabled={column.locked}
        />
        <span>{column.title}</span>
      </div>
    </div>
  );

  const applyChanges = () => setShowColumnsMenu(false);
  const cancelChanges = () => setShowColumnsMenu(false);

  const [columnSearchString, setColumnSearchString] = useState("");
  const searchColumns = (e) =>
    setColumnSearchString(e.target.value.toLowerCase());
  const filterByColumnString = (value) =>
    value.toLowerCase().includes(columnSearchString);

  const findColumnByKey = (key) => {
    const colObj = columns.find((col) => col.key === key);
    if (colObj) {
      toggleColumnVisibility(key);
      setExpandedCategory(colObj.category);
    }
    setShowCustomColumnForm(false);
    setColumnSearchString("");
  };

  return (
    // --- MODIFIED: Set a reasonable fixed width for the modal ---
    <div className="absolute right-0 h-max bg-white shadow-md rounded p-4 mt-1.5 z-20 w-[800px] border border-gray-200">
      {/* --- MODIFIED: Removed w-screen and w-[1100px] for flexible width --- */}
      <div className="font-bold text-lg text-gray-700 flex items-center border-b pb-2 mb-2 w-full">
        {/* Left Side: Title and Search */}
        <div className="flex p-1 w-1/2 items-center border-r">
          {showCustomColumnForm ? (
            <div className="w-full flex items-center">
              <input
                type="text"
                className="w-full border-b border-gray-400 p-1 focus:outline-none focus:ring-0 text-sm"
                onChange={searchColumns}
                ref={columnSearchRef}
                placeholder="Enter Column Name"
              />
              <button onClick={() => setShowCustomColumnForm(false)}>
                <IoIosClose className="text-3xl hover:text-white hover:bg-red-500 rounded-full" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              <h2 className="text-lg">Modify columns</h2>
              <button
                onClick={() => {
                  setShowCustomColumnForm(true);
                  setTimeout(() => columnSearchRef.current?.focus(), 0);
                }}
              >
                <FaSearch />
              </button>
            </div>
          )}
        </div>
        <div className="w-1/2 pl-4">
          <h2 className="font-bold text-lg">Your columns</h2>
        </div>
      </div>

      <div className="flex">
        <div className="w-2/3 pr-4">
          <div className="space-y-1.5 h-72 overflow-auto">
            {showCustomColumnForm ? (
              <div className="w-full h-full overflow-auto">
                {columns.filter((col) => filterByColumnString(col.title))
                  .length <= 0 ? (
                  <div className="text-sm">No Columns Found</div>
                ) : (
                  columns
                    .filter((col) => filterByColumnString(col.title))
                    .map((col) => (
                      <div
                        key={col.key}
                        className="w-full cursor-pointer p-1.5 hover:bg-slate-200 text-sm"
                        onClick={() => findColumnByKey(col.key)}
                      >
                        {col.title}
                      </div>
                    ))
                )}
              </div>
            ) : (
              orderedCategories.map((category) => (
                <div key={category}>
                  <div
                    className="p-1.5 flex items-center hover:bg-gray-50 cursor-pointer w-full justify-between text-sm font-medium"
                    onClick={() => toggleCategory(category)}
                  >
                    {category}
                    <IoIosArrowDown
                      className={`ml-1.5 transform transition-transform duration-300 ease-out ${
                        expandedCategory === category
                          ? "rotate-180"
                          : "rotate-0"
                      }`}
                    />
                  </div>
                  {expandedCategory === category && (
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {columns
                        .filter((col) => col.category === category)
                        .map((col) => (
                          <ColumnItem key={col.key} column={col} />
                        ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="w-1/3 pl-4 border-l">
          <p className="text-sm text-gray-500 mb-2">Drag and drop to reorder</p>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="columnsList">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-1.5 overflow-auto h-64 border rounded-md p-1.5"
                >
                  {columns
                    .filter((column) => column.visible)
                    .map((column, index) => (
                      <Draggable
                        key={String(column.id)}
                        draggableId={String(column.id)}
                        index={index}
                        isDragDisabled={column.locked}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="flex items-center p-1.5 bg-gray-100 mb-1 rounded shadow text-sm"
                          >
                            <div className="flex justify-between items-center w-full">
                              <span className="flex items-center gap-2">
                                {!column.locked ? (
                                  <FaGripLines />
                                ) : (
                                  <MdLockOutline className="text-lg" />
                                )}
                                {column.title}
                              </span>
                              {!column.locked && (
                                <button
                                  onClick={() =>
                                    toggleColumnVisibility(column.key)
                                  }
                                  className="text-gray-700 hover:text-gray-900 text-base font-bold ml-1.5"
                                  title="Hide column"
                                >
                                  <RxCrossCircled />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <div className="flex items-center mt-3">
            <input type="checkbox" id="saveColumnSet" className="mr-1.5" />
            <label htmlFor="saveColumnSet" className="text-sm">
              Save your column set
            </label>
          </div>
        </div>
      </div>

      <div className="flex space-x-1.5 mt-4 border-t pt-2">
        <button
          className="bg-blue-500 text-white px-3 py-1.5 rounded hover:bg-blue-600 text-sm"
          onClick={applyChanges}
        >
          Apply
        </button>
        <button
          className="text-blue-500 ml-2 px-3 py-1.5 rounded text-sm hover:bg-gray-100"
          onClick={cancelChanges}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
